import { ApiError } from '@/api/types'
import { formatIsoDate } from '@/features/inventory-deliveries/components/delivery-columns'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The server's 409 `after_cutoff` body (design D11). */
export interface AfterCutoff {
  deadline: string
  deliveryDate: string
  cutoffTime: string
}

function body(error: ApiError): Record<string, unknown> {
  return typeof error.data === 'object' && error.data !== null
    ? (error.data as Record<string, unknown>)
    : {}
}

export function afterCutoffOf(error: unknown): AfterCutoff | null {
  if (!(error instanceof ApiError) || error.code !== 'after_cutoff') return null
  const data = body(error)
  return {
    deadline: String(data.deadline ?? ''),
    deliveryDate: String(data.deliveryDate ?? ''),
    cutoffTime: String(data.cutoffTime ?? ''),
  }
}

const list = (value: unknown): Array<string> =>
  Array.isArray(value) ? value.map(String) : []

/**
 * Every error code of the ordering config and the purchase-order endpoints as one German sentence
 * (namespace `purchase-orders`, `errors.*`). Unknown codes fall back to the shared error text the
 * API client already put on the error.
 */
export function purchaseOrderErrorMessage(
  error: unknown,
  t: Translate,
): string {
  if (!(error instanceof ApiError)) return t('errors.generic')
  const data = body(error)
  switch (error.code) {
    case 'invalid_email':
      return t(
        data.field === 'copyRecipients'
          ? 'errors.invalid_email_copy'
          : 'errors.invalid_email_edi',
        { value: String(data.value ?? '') },
      )
    case 'ordering_not_configured':
      return t('errors.ordering_not_configured', {
        missing: list(data.missing)
          .map((m) => t(`config.missing.${m}`))
          .join(', '),
      })
    case 'order_not_sendable':
      return t('errors.order_not_sendable', {
        status: t(`status.${String(data.status ?? 'DRAFT')}`),
      })
    case 'after_cutoff':
      return t('errors.after_cutoff', {
        cutoffTime: String(data.cutoffTime ?? ''),
        date: formatIsoDate(String(data.deliveryDate ?? '')),
      })
    case 'mail_failed':
      return t('errors.mail_failed', { reason: String(data.reason ?? '') })
    case 'article_not_orderable':
      return t('errors.article_not_orderable', {
        sku: String(data.sku ?? ''),
        reason: t(
          `notOrderable.reason.${String(data.reason ?? 'unknown_article')}`,
        ),
      })
    case 'unknown_package_level':
    case 'level_without_gtin':
      return t(`errors.${error.code}`, { sku: String(data.sku ?? '') })
    case 'product_has_no_stock':
    case 'product_not_assigned':
      return t(`errors.${error.code}`, { skus: list(data.skus).join(', ') })
    case 'invalid_supplier_gln':
    case 'too_many_copy_recipients':
    case 'invalid_cutoff_time':
    case 'supplier_name_too_long':
    case 'lines_required':
    case 'delivery_date_in_past':
    case 'delivery_date_required':
    case 'order_not_draft':
    case 'order_not_found':
    case 'duplicate_line':
    case 'too_many_lines':
    case 'invalid_quantity':
      return t(`errors.${error.code}`)
    default:
      return error.message || t('errors.generic')
  }
}
